import React, { useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { MassMailModal } from 'screens/mass-mail';
import { HelpdeskEmail, MassMailAiSuggestion } from 'screens/mass-mail/types';
import {
  closeHelpdeskReplySuggestion,
  fetchHelpdeskReplySuggestion,
  generateHelpdeskReplySuggestion,
} from 'shared/api/ai-reply-suggestion';
import { HELPDESK_DEFAULT_AI_INSTRUCTION } from 'shared/utils/aiReplyInstructionDefaults';
import { convertTicketRecipientToContact } from 'screens/mass-mail/utils/ticketToContact';
import { getHelpdeskTemplates } from '../../shared/helpdeskTemplates';
import { ticketSuppliersToContacts } from '../../shared/ticketSuppliers';
import { HelpdeskTicketInterface } from 'shared/types/helpdesk';
import { Email } from './EmailsSection';

interface HelpdeskMassMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSend: (emailData: any) => Promise<any>;
  ticket: HelpdeskTicketInterface;
  communicationMode: 'tenant' | 'supplier';
  isInsurance: boolean;
  emailHistory?: Email[];
  replyToEmail?: Email;
  /** Geselecteerde leverancier-ids (ContactCompany) als ontvangers in leverancier-modus. */
  recipientSupplierIds?: number[];
}

export const HelpdeskMassMailModal: React.FC<HelpdeskMassMailModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSend,
  ticket,
  communicationMode,
  isInsurance,
  emailHistory,
  replyToEmail,
  recipientSupplierIds,
}) => {
  const { session } = useSession();

  // Memoize all props to prevent unnecessary re-renders
  const contextTemplates = useMemo(() =>
    getHelpdeskTemplates(communicationMode),
    [communicationMode]
  );

  /**
   * De knop **AI-voorstel** boven het tekstvak, plus het voorstel dat de backend al
   * automatisch schreef toen de huurdersmail binnenkwam.
   *
   * Niet bij een verzekeringsdossier: `ticket.id` is daar het dossiernummer, en dat
   * hoort niet bij een helpdeskticket — het voorstel zou over een ander dossier gaan.
   *
   * De doelgroep volgt de huurder/leverancier-schakelaar: een antwoord aan een
   * huurder ("we volgen op") is geen bericht aan een aannemer ("graag je planning"),
   * en de backend bewaart en filtert erop.
   */
  const aiSuggestion = useMemo<MassMailAiSuggestion | undefined>(() => {
    const ticketId = ticket?.id;
    if (isInsurance || !session || !ticketId) return undefined;
    return {
      generate: (instructie) =>
        generateHelpdeskReplySuggestion(
          ticketId,
          communicationMode,
          session,
          instructie,
        ),
      loadPending: () =>
        fetchHelpdeskReplySuggestion(ticketId, communicationMode, session),
      close: (suggestion, outcome) =>
        suggestion.id
          ? closeHelpdeskReplySuggestion(suggestion.id, outcome, session)
          : Promise.resolve(),
      defaultInstruction: HELPDESK_DEFAULT_AI_INSTRUCTION,
    };
  }, [isInsurance, session, ticket?.id, communicationMode]);

  const clipboardBlock = useMemo(() => {
    if (communicationMode !== 'supplier') return undefined;

    const reporter = ticket.reporter as any;
    const building = ticket.building as any;
    const unit = ticket.unit as any;
    const company = building?.company;

    const reporterName = reporter
      ? `${reporter.firstName || ''} ${reporter.lastName || ''}`.trim() || '—'
      : '—';
    const reporterPhone = reporter?.phone_mobile || reporter?.phone_direct || '—';
    const reporterEmail = reporter?.email || '—';

    const companyName = company
      ? `${company.name || ''}${company.legalForm ? ' ' + company.legalForm : ''}`
      : '—';
    const companyAddress = company?.googlePlaceLongAddress || '—';
    const companyKbo = company?.kbo || '—';
    const companyVat = company?.vat || '—';

    const ticketRef = `H-${ticket.id}`;
    const buildingAddress = building?.googlePlaceLongAddress || building?.id || '—';
    const unitId = unit?.id || '—';
    const unitBox = unit?.boxNumber || '—';
    const unitFloor = unit?.floor != null ? unit.floor : '—';

    return `
<br/><hr/>
<p><strong>[CONTACT TER PLAATSE &amp; FACTURATIEGEGEVENS]</strong></p>

<p><strong>A) Contact ter plaatse (huurder)</strong><br/>
Naam: ${reporterName}<br/>
Telefoon: ${reporterPhone}<br/>
E-mail: ${reporterEmail}</p>

<p><strong>B) Validatie der werken</strong><br/>
Gelieve <strong>FOTO'S</strong> van de uitgevoerde werken als reactie op deze mail door te sturen ter validatie van uw werk.</p>

<p><strong>C) Facturatiegegevens</strong><br/>
Naam: ${companyName}<br/>
Adres: ${companyAddress}<br/>
KBO: ${companyKbo}<br/>
BTW: ${companyVat}<br/>
<em>Facturatie verloopt via Peppol.</em></p>

<p><strong>D) Referentie op factuur (verplicht)</strong><br/>
⚠️ Vermeld <strong>${ticketRef}</strong> + <strong>${buildingAddress}</strong> op uw factuur. Facturen zonder dit referentienummer en het adres van de interventieplaats worden niet goedgekeurd.</p>

<p><strong>E) Adres interventieplaats</strong><br/>
Adres: ${buildingAddress}<br/>
Unit: ${unitId}<br/>
Bus: ${unitBox}<br/>
Verdieping: ${unitFloor}</p>`;
  }, [communicationMode, ticket.reporter, ticket.building, ticket.unit, ticket.id]);

  const initialState = useMemo(() => {
    // Leverancier-modus: vul ALLE toegewezen leveranciers als ontvangers voor
    // (multi-leverancier). De gebruiker kan er in de modal nog uit selecteren.
    // Huurder-modus: één ontvanger zoals voorheen.
    const contacts =
      communicationMode === 'supplier'
        ? ticketSuppliersToContacts(ticket, recipientSupplierIds)
        : (() => {
            const contact = convertTicketRecipientToContact(ticket, communicationMode);
            return contact ? [contact] : [];
          })();
    return {
      contacts,
      emailSubject: `Ticket ${isInsurance ? 'INS' : communicationMode === 'supplier' ? 'HS' : 'H'}${ticket.id} - ${ticket.title}`,
      contextPlaceholders: {
        ticketId: ticket.id?.toString() || '',
        ticketTitle: ticket.title || '',
        buildingId: ticket.building?.id || '',
        unitInfo: ticket.unit ? ` - Unit ${ticket.unit.id}` : '',
      },
      clipboardBlock,
      aiSuggestion,
      // Helpdesk replies start blank — quick-replies are opt-in, not pre-filled.
      autoSelectFirstTemplate: false,
      // Ticketmails vertrekken altijd vanuit de helpdeskmailbox: de endpoints
      // /helpdesk/ticket/:id/send-email en /send-supplier-email posten via Ella
      // met `from: helpdesk@yally.be` (helpdesk.service.ts). Vastzetten dus, zodat
      // de composer geen ander adres toont of laat kiezen dat toch genegeerd wordt.
      lockedSenderEmail: 'helpdesk@yally.be',
    };
  }, [ticket.id, ticket.title, ticket.building?.id, ticket.unit?.id, ticket.supplierAssignments, recipientSupplierIds, communicationMode, isInsurance, clipboardBlock, aiSuggestion]);

  // Only render when open
  if (!isOpen) return null;

  return (
    <MassMailModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      onSend={onSend}
      contextTemplates={contextTemplates}
      initialState={initialState}
      emailHistory={emailHistory}
      replyToEmail={replyToEmail as HelpdeskEmail}
    />
  );
};
